'use client';

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { ClockIcon } from '@phosphor-icons/react';

import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { buildTooltip } from '@/components/charts/EChartTooltip';
import { useCompactCurrency } from '@/hooks/useCompactCurrency';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useOpenInvoicesWidget } from '@/hooks/queries/useOpenInvoicesWidget';
import { cardBrandImage, normalizeCardBrand } from '@/lib/card-brands';
import { hexWithAlpha } from '@/lib/colors';
import type { ThemePalette } from '@/lib/theme/tokens';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import type {
  OpenInvoiceCard,
  OpenInvoiceMonth,
  OpenInvoicesWidget as OpenInvoicesData,
} from '@/types';
import { WidgetCard, widgetState } from './WidgetCard';

/**
 * W3 · Em aberto — open credit-card invoices: headline total, per-card chips
 * and a 5-month evolution bar chart (past · current · forecast). One component
 * serves both surfaces: the desktop card (subline + chips + legend + 104px
 * chart) and the mobile Panorama carousel slide (due-date chip + 110px mini
 * chart) via responsive classes.
 */
export function EmAbertoWidget(): ReactNode {
  const { data, error, isLoading } = useOpenInvoicesWidget();

  return (
    <WidgetCard
      eyebrow="Em aberto"
      action={{ label: 'Faturas', href: '/invoices' }}
      state={widgetState(data, error, isLoading, isInvoicesEmpty)}
      emptyLabel="Sem faturas em aberto"
    >
      <OpenInvoicesBody data={data} />
    </WidgetCard>
  );
}

// No credit cards at all → nothing to show (contract: cardCount 0, months []).
function isInvoicesEmpty(data: OpenInvoicesData): boolean {
  return data.cardCount === 0 && data.months.length === 0;
}

function OpenInvoicesBody({ data }: { data: OpenInvoicesData | undefined }): ReactNode {
  const formatCurrency = useFormatCurrency();
  // Prototype-style compact money ("R$ 7,2k") for the current-bar label; from
  // R$ 100 mil up the decimals drop ("R$ 123k"). Privacy-aware, masked first.
  const compactCurrency = useCompactCurrency({ style: 'k', integerFrom: 100_000 });
  const palette = useThemeColors();

  const months = useMemo(() => data?.months ?? [], [data]);
  const desktopOption = useMemo(
    () => buildInvoiceChartOption(months, palette, formatCurrency, compactCurrency, 'desktop'),
    [months, palette, formatCurrency, compactCurrency],
  );
  const mobileOption = useMemo(
    () => buildInvoiceChartOption(months, palette, formatCurrency, compactCurrency, 'mobile'),
    [months, palette, formatCurrency, compactCurrency],
  );

  if (!data) return null;

  const dueLabel = data.nextDueDate ? formatDayMonth(data.nextDueDate) : null;
  const rangeLabel = forecastRangeLabel(data.months);

  return (
    <div className="flex h-full flex-col">
      {/* Headline total + (mobile-only) due-date chip. */}
      <div className="mt-1 flex flex-wrap items-center gap-2 sm:mt-2">
        <span className="text-[29px] font-semibold tracking-tight text-text-primary tabular-nums sm:text-3xl">
          {formatCurrency(data.total, { noCents: true })}
        </span>
        {dueLabel && (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning-surface px-2.5 py-1 text-xs font-medium text-warning-text sm:hidden">
            <ClockIcon size={11} weight="fill" aria-hidden />
            vence {dueLabel}
          </span>
        )}
      </div>

      {/* Desktop subline: card count · next due date (clause omitted when unknown). */}
      <p className="mt-1 hidden text-xs text-text-tertiary sm:block">
        {cardCountLabel(data.cardCount)}
        {dueLabel ? ` · próxima vence ${dueLabel}` : ''}
      </p>

      {/* Per-card chips — the split per card is key info on every breakpoint;
          the connector branding tells same-looking cards apart at a glance. */}
      {data.cards.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 font-mono text-xs text-text-tertiary">
          {/* Backend names cards by connector, so two cards at the same bank
              share a name — suffix the (stable, desc-by-total) index. */}
          {data.cards.map((card, index) => (
            <span
              key={`${card.name}-${index}`}
              // The CARD is the identity the user knows ("Uniclass Black"), the
              // bank is just context — surface the card name, keep the
              // connector in the hover title.
              title={`${card.name}${card.cardName ? ` · ${card.cardName}` : ''}`}
              className="flex items-center gap-1.5 rounded-lg bg-surface-sunken py-1 pr-2.5 pl-1.5 tabular-nums"
            >
              <CardBrandDot card={card} />
              <span className="max-w-36 truncate">{cardChipLabel(card)}</span>
              {formatCurrency(card.total, { noCents: true })}
            </span>
          ))}
        </div>
      )}

      {/* Evolution chart, pushed to the card bottom. */}
      {data.months.length > 0 && (
        <div className="mt-auto pt-3 sm:pt-5">
          {/* Visible on mobile too: forecast bars render there as well, and the
              "previstas" qualifier is what keeps them honest (info-loss pass). */}
          <div className="mb-2 flex items-center justify-between font-mono text-[10px] text-text-tertiary">
            <span>
              evolução · {data.months.length} {data.months.length === 1 ? 'mês' : 'meses'}
            </span>
            {rangeLabel && <span>{rangeLabel}</span>}
          </div>
          <div className="h-[110px] sm:hidden">
            <EChart option={mobileOption} className="em-aberto-chart--mobile" />
          </div>
          <div className="hidden h-[104px] sm:block">
            <EChart option={desktopOption} className="em-aberto-chart--desktop" />
          </div>
        </div>
      )}
    </div>
  );
}

/** The card's own name, title-cased ("UNICLASS BLACK PONTOS" → "Uniclass Black
 * Pontos"), falling back to the connector when Pluggy sends no account name. */
function cardChipLabel(card: OpenInvoiceCard): string {
  if (!card.cardName) return card.name;
  return card.cardName
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

/** The card NETWORK logo (Mastercard/Visa/… — same assets as the faturas page)
 * when the brand is known; else the connector icon; else a dot in the
 * connector's primary color (bank colors are runtime DATA, not theme tokens);
 * else a neutral dot. */
function CardBrandDot({ card }: { card: OpenInvoiceCard }): ReactNode {
  const brand = normalizeCardBrand(card.brand);
  if (brand) {
    return (
      <img
        src={cardBrandImage(brand)}
        alt={brand}
        data-testid="card-brand-logo"
        className="h-3 w-auto shrink-0"
      />
    );
  }
  if (card.imageUrl) {
    return (
      <img
        src={card.imageUrl}
        alt=""
        aria-hidden
        className="size-4 shrink-0 rounded-full bg-surface object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: card.color ?? 'var(--neutral-400)' }}
    />
  );
}

type FormatCurrencyFn = ReturnType<typeof useFormatCurrency>;
type ChartVariant = 'desktop' | 'mobile';

const MONO_FONT = "'DM Mono', ui-monospace, SFMono-Regular, monospace";

function cardCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'cartão' : 'cartões'}`;
}

/** "2026-07-09" → "09 jul" (pt-BR 3-letter month, no trailing period). */
function formatDayMonth(isoDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const monthIndex = Number(match[2]) - 1;
  const month = PT_BR_MONTHS[monthIndex];
  if (!month) return null;
  return `${match[3]} ${month}`;
}

const PT_BR_MONTHS = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
] as const;

/** Legend right label — "ago–set previstas" from the forecast months, or null. */
function forecastRangeLabel(months: OpenInvoiceMonth[]): string | null {
  const forecast = months.filter((m) => m.kind === 'forecast');
  if (forecast.length === 0) return null;
  if (forecast.length === 1) return `${forecast[0].label} prevista`;
  return `${forecast[0].label}–${forecast[forecast.length - 1].label} previstas`;
}

function barItemStyle(kind: OpenInvoiceMonth['kind'], palette: ThemePalette, variant: ChartVariant) {
  const borderRadius: [number, number, number, number] =
    variant === 'mobile' ? [3, 3, 0, 0] : [4, 4, 0, 0];
  if (kind === 'current') return { color: palette.primary, borderRadius };
  if (kind === 'past') return { color: hexWithAlpha(palette.primary, 0.5), borderRadius };
  // Forecast: faint primary fill + dashed primary outline, so it reads "predicted".
  return {
    color: hexWithAlpha(palette.primary, 0.14),
    borderRadius,
    borderType: 'dashed' as const,
    borderColor: hexWithAlpha(palette.primary, 0.6),
    borderWidth: variant === 'mobile' ? 1.2 : 1.3,
  };
}

/** Tooltip row label per month kind — the bars mix three different semantics
 * (closed faturas, the open one, and forecasts built ONLY from already-known
 * parcelas), so the point of inspection must say which one it is. */
const KIND_TOOLTIP_LABEL: Record<OpenInvoiceMonth['kind'], string> = {
  past: 'Fatura fechada',
  current: 'Em aberto',
  forecast: 'Prevista · parcelas já lançadas',
};

function buildInvoiceChartOption(
  months: OpenInvoiceMonth[],
  palette: ThemePalette,
  format: FormatCurrencyFn,
  compact: (value: number) => string,
  variant: ChartVariant,
): EChartsOption {
  if (months.length === 0) return {};
  const isMobile = variant === 'mobile';
  const max = Math.max(...months.map((m) => m.total));

  const baseTooltip = buildTooltip({ format: (v: number) => format(v, { noCents: true }) });

  return {
    tooltip: isMobile
      ? { show: false }
      : {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (raw: unknown) => {
            const params = (Array.isArray(raw) ? raw : [raw]) as Array<
              Record<string, unknown> & { dataIndex?: number }
            >;
            const kind = months[params[0]?.dataIndex ?? -1]?.kind;
            if (kind) params[0].seriesName = KIND_TOOLTIP_LABEL[kind];
            return baseTooltip(params);
          },
        },
    grid: { left: 2, right: 2, top: isMobile ? 6 : 22, bottom: 0, containLabel: true },
    xAxis: {
      type: 'category',
      data: months.map((m) => m.label),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontFamily: MONO_FONT,
        fontSize: isMobile ? 9 : 10,
        color: palette.textTertiary,
        margin: isMobile ? 4 : 6,
      },
    },
    yAxis: {
      type: 'value',
      // Bars encode value by LENGTH — the axis must start at zero (a truncated
      // domain exaggerated month-to-month deltas; product-review finding).
      min: 0,
      max: Math.ceil(max * 1.07),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: isMobile
        ? { show: false }
        : { show: true, lineStyle: { type: 'dashed', color: palette.border } },
    },
    series: [
      {
        name: 'Fatura',
        type: 'bar',
        barCategoryGap: isMobile ? '22%' : '26%',
        data: months.map((m) => ({
          value: m.total,
          itemStyle: barItemStyle(m.kind, palette, variant),
          // Compact value label above the CURRENT bar only (desktop).
          label: {
            show: !isMobile && m.kind === 'current',
            position: 'top' as const,
            fontFamily: MONO_FONT,
            fontSize: 11,
            fontWeight: 600 as const,
            color: palette.primary,
            formatter: () => compact(m.total),
          },
        })),
      },
    ],
  };
}
